/** @format */
import React, { FunctionComponent, useEffect, useState } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import { PlatformIccidPoolNumber } from '../../../interfaces/platform/PlatformIccidPoolNumber';
import { iccidOperatorStatus, iccidState, iccidStatus } from '../../../helpers/iccidStatus';
import GridItem from '../../../components/Grid/GridItem';
import Typography from '@material-ui/core/Typography';
import I18n from '../../../components/Translate/I18n';
import { DateHelper } from '../../../helpers/DateHelper';
import BackTextButton from '../../../components/Buttons/BackTextButton';
import { history } from '../../../core/history';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { PlatformRmoCollectionInterface, PlatformRmoInterface } from '../../../interfaces/collections/PlatformRmoInterface';
import { useDispatch } from 'react-redux';
import { StoreStackReducerAction, StoreStackReducerType } from '../../../modules/lib/StoreStackReducer';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { IccidPoolsChangeStatus } from './IccidPoolsChangeStatus';
import { QrCode } from '../../../interfaces/platform/QrCode';
import { IccidPoolsReleaseESim } from './IccidPoolsReleaseESim';

const _ = require('underscore');
type ImsiViewType = {
    item: PlatformIccidPoolNumber;
    reload(): void;
};
export const ImsiView: FunctionComponent<ImsiViewType> = ({ item, reload }): React.ReactElement => {
    const [openChangeStatus, setOpenChangeStatus] = useState(false);
    const [openReleaseESim, setOpenReleaseESim] = useState(false);
    const isSuperUser = false;
    const dispatch = useDispatch();
    const [rmoModels, setRmoModels] = useState<PlatformRmoCollectionInterface | null>(null);
    useEffect(() => {
        if (!rmoModels && isSuperUser) {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_rmos')),
                'get_platform_rmo_models_partner_mvne_list',
                [
                    { value: false, name: 'deleted' },
                    { name: 'active', value: 'true' },
                ] as Array<Filter>,
                [{ name: 'description', value: 'asc' }] as Array<Order>,
                (data: PlatformRmoCollectionInterface): void => {
                    setRmoModels(data);
                    dispatch({
                        type: StoreStackReducerAction.LOAD_PLATFORM_RMO_MODEL_PARTNER_MVNE_LIST,
                        platformRmoCollection: data,
                    } as StoreStackReducerType);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, rmoModels, isSuperUser]);
    const getRmoDescription = (rmoId: number): string => {
        if (rmoModels) {
            const element = _.find(rmoModels['hydra:member'], (item: PlatformRmoInterface) => {
                return item.id === rmoId;
            });
            return element ? element.description : '';
        }
        return '';
    };
    const callBack = (): void => history.goBack();

    const downloadQrCodeBase64Pdf = (item: PlatformIccidPoolNumber): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/qrcodes')),
            'download_qr_code',
            { imsi: item.imsi },
            (data: QrCode): void => {
                const linkSource = `data:application/pdf;base64,${data.qrCodePdf}`;
                const downloadLink = document.createElement('a');
                const fileName = 'qrCode.pdf';

                downloadLink.href = linkSource;
                downloadLink.download = fileName;
                downloadLink.click();
            }
        );
    };

    return (
        <GridContainer>
            <GridItem xs={12} style={{ paddingLeft: 0 }}>
                <GridContainer>
                    {item.lastmsisdn && (
                        <React.Fragment>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:list.msisdn" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {item.lastmsisdn}
                                </Typography>
                            </GridItem>
                        </React.Fragment>
                    )}
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:pool.imsi" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {item.imsi}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:view.iccid" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {item.iccid}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:pool.iccid_type" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {item.iccidType}
                        </Typography>
                    </GridItem>
                    {isSuperUser && (
                        <React.Fragment>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="pools:pool.mvne_status_rmo" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {item.rmoModelMvne && getRmoDescription(item.rmoModelMvne)}
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="pools:pool.mvne_status_date_rmo" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {item.rmoModelMvneSetup && DateHelperTs.format(item.rmoModelMvneSetup, 'YYYY-MM-DD')}
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="pools:pool.partner_status_rmo" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {item.rmoModelPartner && getRmoDescription(item.rmoModelPartner)}
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="pools:pool.partner_status_date_rmo" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {item.rmoModelPartnerSetup && DateHelperTs.format(item.rmoModelPartnerSetup, 'YYYY-MM-DD')}
                                </Typography>
                            </GridItem>
                        </React.Fragment>
                    )}
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:view.mvno_status" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans={iccidOperatorStatus(item.mvnoStatus)} />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:sim.iccid_state" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {<I18n trans={iccidStatus(item.iccidState)} />}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:pool.activation_date" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6} style={{ paddingLeft: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            {DateHelper.format(item.activationDate)}
                        </Typography>
                    </GridItem>
                    {isSuperUser && (
                        <React.Fragment>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="pools:view.partner" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {item.ownerPlatformResellerName}
                                </Typography>
                            </GridItem>
                        </React.Fragment>
                    )}
                    {item.iccidType === 'ESIM' && (
                        <>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="pools:pool.matching_id" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {item.platformIccidEsim && item.platformIccidEsim.matchingId}
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="pools:pool.confirmation_code" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6} style={{ paddingLeft: 0 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {item.platformIccidEsim && item.platformIccidEsim.confirmationCode}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </GridItem>
            <GridItem xs={12} style={{ paddingLeft: 0, marginTop: 15 }}>
                <BackTextButton callback={callBack} callbackParam={{}} size="sm" />
                {item.iccidState === iccidState.ARCHIVE &&
                    item.iccidType !== 'ESIM' &&
                    UserRoleHelper.checkRole(roles.ROLE_PLATFORM_ICCID_POOL_NUMBER_PUT) && (
                        <>
                            <CustomTextButtom
                                callback={(): void => setOpenChangeStatus(true)}
                                callbackParam={{}}
                                color="success"
                                label="pools:sim.change_status"
                                size="sm"
                            />
                            <IccidPoolsChangeStatus
                                iccid={item}
                                open={openChangeStatus}
                                reload={reload}
                                close={(): void => setOpenChangeStatus(false)}
                            />
                        </>
                    )}
                {!item.esimUpdate && UserRoleHelper.checkRole(roles.ROLE_QR_CODE_GET) && item.platformIccidEsim && item.platformIccidEsim.matchingId && (
                    <>
                        <CustomTextButtom
                            callback={downloadQrCodeBase64Pdf}
                            callbackParam={item}
                            color="success"
                            label="pools:sim.get_pdf_qr_code"
                            size="sm"
                        />
                    </>
                )}
                {item.iccidType === 'ESIM' && (item.iccidState === iccidState.ARCHIVE || item.iccidState === iccidState.RESERVATION) && (
                    <>
                        <CustomTextButtom
                            callback={(): void => setOpenReleaseESim(true)}
                            callbackParam={{}}
                            color="success"
                            label="pools:sim.cancel_release_e_sim"
                            size="sm"
                        />
                        <CustomTextButtom
                            callback={(): void => setOpenChangeStatus(true)}
                            callbackParam={{}}
                            color="success"
                            label="pools:sim.local_release_e_sim"
                            size="sm"
                        />
                        <IccidPoolsChangeStatus
                            eSim={true}
                            iccid={item}
                            open={openChangeStatus}
                            reload={reload}
                            close={(): void => setOpenChangeStatus(false)}
                        />
                        <IccidPoolsReleaseESim iccid={item} open={openReleaseESim} reload={reload} close={(): void => setOpenReleaseESim(false)} />
                    </>
                )}
            </GridItem>
        </GridContainer>
    );
};
